import { Component, OnInit, ViewChild, ElementRef, ChangeDetectorRef, OnChanges, SimpleChanges } from '@angular/core';
import { ListingTimeline, ListingTimelineable } from 'app/models/listing-timeline';
import { Observable, fromEvent, BehaviorSubject, Subject, combineLatest } from 'rxjs';
import { ListingLocations, ListingsService, ListingOptions, ListingsResponse } from 'app/services/listings.service';

import { keys, map as lodashMap, isEqual, clone, omitBy, castArray } from 'lodash-es';
import { map,  filter, share, tap } from 'rxjs/operators';
import { IMultiSelectTexts, IMultiSelectSettings, IMultiSelectOption } from 'ngx-bootstrap-multiselect';

import { PageEvent, MatPaginator } from '@angular/material/paginator';
import { MediaMatcher } from '@angular/cdk/layout';
import { Status, externalListingStatuses } from 'app/models/status';

import { startWith } from 'rxjs/operators';

import Pin from 'app/models/pin';
import { ActivatedRoute, Params, Router } from '@angular/router';
import { SUPPORTED_STATES } from 'app/models/states';
import { handleIMultiSelectSettings, handleIMultiSelectTexts } from 'app/shared/utilities/multi-select';

interface MultiSelectOption {
  id: string,
  name: string;
}

@Component({
  selector: 'houwzer-listings', // <listing></listing> is a reserved HTML tag name, we're staying away from that
  templateUrl: './listings.component.html',
  styleUrls: ['./listings.component.scss']
})
export class ListingsComponent implements OnInit {

  @ViewChild('applyButton', { static: true }) applyButton: ElementRef;
  @ViewChild('topPaginator', { static: true }) topPaginator: MatPaginator;
  @ViewChild('bottomPaginator', { static: true }) bottomPaginator: MatPaginator;

  loadingListings?: boolean;

  showingMobilePropertyFilters = false;
  showingMobileListingFilters = false;
  showingMobileLocationFilters = false;

  // listingsResponse: Observable<ListingsResponse>;
  listingTimelines: ListingTimeline[];
  listingLocations: Observable<ListingLocations>;

  pins: Observable<Pin[]>;

  // selected fields are those which have been applied and we're currently showing results for
  selectedBeds: string[] = [];
  selectedFullBaths: string[] = [];
  selectedHalfBaths: string[] = [];
  selectedSqft: string[] = [];
  selectedStatuses: string[] = [];
  selectedMinPrice: string[] = [];
  selectedMaxPrice: string[] = [];
  selectedStates: string[] = [];
  selectedCounties: string[] = [];
  selectedCountySubdivisions: string[] = [];
  selectedSortFields: string[] = [];
  selectedSortDirections: string[] = [];

  // desired fields are those which have been changed but not yet applied. They are used to determine
  // if we have pending fitler changes that have not been applied yet.
  desiredBeds: string[] = [];
  desiredFullBaths: string[] = [];
  desiredHalfBaths: string[] = [];
  desiredSqft: string[] = [];
  desiredStatuses: string[] = ['active', 'coming_soon'];
  desiredMinPrice: string[] = [];
  desiredMaxPrice: string[] = [];
  desiredStates: string[] = [];
  desiredCounties: string[] = [];
  desiredCountySubdivisions: string[] = [];
  desiredSortFields: string[] = [];
  desiredSortDirections: string[] = [];

  desiredStateSubject = new Subject<string[]>();
  desiredCountySubject = new Subject<string[]>();

  filterStatusesOptions: IMultiSelectOption[] = externalListingStatuses.map((listingStatus: Status) => {
    return { id: listingStatus.slug, name: listingStatus.name }
  });

  filterStatusesTexts: IMultiSelectTexts = handleIMultiSelectTexts('status', {
    definedPlural: 'statuses',
    defaultTitle: 'Select Statuses',
  });

  filterStatusesSettings: IMultiSelectSettings = handleIMultiSelectSettings();

  filterBedOptions = [
    { id: '1', name: '1+ Beds' },
    { id: '2', name: '2+ Beds' },
    { id: '3', name: '3+ Beds' },
    { id: '4', name: '4+ Beds' },
    { id: '5', name: '5+ Beds' },
  ]

  filterBedsTexts: IMultiSelectTexts = {
    defaultTitle: 'Any Beds'
  };

  filterFullBathOptions = [
    { id: '1', name: '1+ Full Baths' },
    { id: '2', name: '2+ Full Baths' },
    { id: '3', name: '3+ Full Baths' },
    { id: '4', name: '4+ Full Baths' }
  ]

  filterFullBathsTexts: IMultiSelectTexts = {
    defaultTitle: 'Any Full Baths'
  };

  filterHalfBathOptions = [
    { id: '1', name: '1+ Half Baths' },
    { id: '2', name: '2+ Half Baths' },
    { id: '3', name: '3+ Half Baths' },
    { id: '4', name: '4+ Half Baths' }
  ]

  filterHalfBathsTexts: IMultiSelectTexts = {
    defaultTitle: 'Any Half Baths'
  };

  filterStatesOptions: Observable<MultiSelectOption[]>;

  filterStatesTexts: IMultiSelectTexts = {
    defaultTitle: 'Any States'
  };

  filterCountiesOptions: Observable<MultiSelectOption[]>;

  filterCountiesTexts: IMultiSelectTexts = {
    defaultTitle: 'All Counties',
    searchEmptyResult: 'Please Select a State'
  };

  singleSelectionLimitSettings: IMultiSelectSettings = handleIMultiSelectSettings({
    closeOnSelect: true,
    autoUnselect: true,
    selectionLimit: 1
  });

  filterCountySubdivisionOptions: Observable<MultiSelectOption[]>;

  filterCountySubdivisionTexts = new BehaviorSubject<IMultiSelectTexts>({
    defaultTitle: 'All Towns/Neighborhoods',
    searchEmptyResult: 'Please Select State and County',
    allSelected: 'All Towns/Neighborhoods'
  })

  filterCountySubdivisionSettings: IMultiSelectSettings = handleIMultiSelectSettings({
    showCheckAll: true,
    showUncheckAll: true,
  });

  filterMinPriceOptions = [
    { id: '150000', name: 'More than $150,000' },
    { id: '200000', name: 'More than $200,000' },
    { id: '250000', name: 'More than $250,000' },
    { id: '300000', name: 'More than $300,000' },
    { id: '350000', name: 'More than $350,000' },
    { id: '400000', name: 'More than $400,000' },
    { id: '450000', name: 'More than $450,000' },
    { id: '500000', name: 'More than $500,000' },
    { id: '600000', name: 'More than $600,000' },
    { id: '700000', name: 'More than $700,000' },
    { id: '800000', name: 'More than $800,000' },
    { id: '900000', name: 'More than $900,000' },
    { id: '1000000', name: 'More than $1M+' },
  ]

  filterMinPriceTexts: IMultiSelectTexts = {
    defaultTitle: 'Select Min Price'
  };

  filterMaxPriceOptions = [
    { id: '200000', name: 'Less than $200,000' },
    { id: '250000', name: 'Less than $250,000' },
    { id: '300000', name: 'Less than $300,000' },
    { id: '350000', name: 'Less than $350,000' },
    { id: '400000', name: 'Less than $400,000' },
    { id: '450000', name: 'Less than $450,000' },
    { id: '500000', name: 'Less than $500,000' },
    { id: '600000', name: 'Less than $600,000' },
    { id: '700000', name: 'Less than $700,000' },
    { id: '800000', name: 'Less than $800,000' },
    { id: '900000', name: 'Less than $900,000' },
    { id: '1000000', name: 'Less than $1M+' },
  ]

  filterMaxPriceTexts: IMultiSelectTexts = {
    defaultTitle: 'Select Max Price'
  };

  filterSqftOptions = [
    { id: '500', name: '500+ sqft' },
    { id: '750', name: '750+ sqft' },
    { id: '1000', name: '1000+ sqft' },
    { id: '1250', name: '1250+ sqft' },
    { id: '1500', name: '1500+ sqft' },
    { id: '1750', name: '1750+ sqft' },
    { id: '2000', name: '2000+ sqft' },
    { id: '2500', name: '2500+ sqft' },
    { id: '3000', name: '3000+ sqft' },
    { id: '3500', name: '3500+ sqft' },
    { id: '4000', name: '4000+ sqft' },
    { id: '4500', name: '4500+ sqft' },
    { id: '5000', name: '5000+ sqft' },
  ]

  filterSqftTexts: IMultiSelectTexts = {
    defaultTitle: 'Any Sqft'
  };


  pageEvent: PageEvent;
  length: number;
  pageIndex = 0;
  pageSize = 20;

  pageSizeOptions: number[] = [ 20, 50, 100 ];

  showResults?: boolean;
  showPaginator: boolean = false;

  phoneQuery: MediaQueryList;
  private _mobileQueryListener: () => void;

  constructor(
    private listingsService: ListingsService,
    private media: MediaMatcher,
    private changeDetectorRef: ChangeDetectorRef,
    private route: ActivatedRoute,
    private router: Router
  ) {
    // 575 is the highest xs screen size and what we should consider a phone
    this.phoneQuery = this.media.matchMedia('(max-width: 575px)');
    this._mobileQueryListener = () => this.changeDetectorRef.detectChanges();
    this.phoneQuery.addListener(this._mobileQueryListener);
  }

  ngOnInit() {

    this.route.queryParamMap.subscribe((params: Params) => {

      /*
      These params are named as if there are multiple values (ie. 'counties%5B%5D' which is 'counties[]') but
      the value won't be an array unless there are actually multiple (ie. counties%5B%5=Philadelphia comes
      out of the params collection as counties[] = 'Philadelphia', not counties[] = ['Philadelphia'] like we need).
      Casting to array ensures that we always have an array
      */
      this.desiredBeds = params.params['beds[]'] ? castArray(params.params['beds[]']) : [];
      this.desiredFullBaths = params.params['fullBaths[]'] ? castArray(params.params['fullBaths[]']) : [];
      this.desiredHalfBaths = params.params['halfBaths[]'] ? castArray(params.params['halfBaths[]']) : [];
      this.desiredSqft = params.params['sqft[]'] ? castArray(params.params['sqft[]']) : [];
      this.desiredStatuses = params.params['statuses[]'] ? castArray(params.params['statuses[]']) : ['active', 'coming_soon'];
      this.desiredMinPrice = params.params['minPrice[]'] ? castArray(params.params['minPrice[]']) : [];
      this.desiredMaxPrice = params.params['maxPrice[]'] ? castArray(params.params['maxPrice[]']) : [];
      this.desiredStates = params.params['states[]'] ? castArray(params.params['states[]']) : [];
      this.desiredCounties = params.params['counties[]'] ? castArray(params.params['counties[]']) : [];
      this.desiredCountySubdivisions = params.params['countySubdivisions[]'] ? castArray(params.params['countySubdivisions[]']) : [];

      this.applyFilters();
    })

    this.listingLocations = this.listingsService
      .getListingLocations()
      .pipe(share())

    this.filterStatesOptions = this.listingLocations.pipe(
      map(listingLocations => keys(listingLocations)),
      map(states => this.formatStateOptions(states))
    )

    this.filterCountiesOptions = combineLatest([
      this.desiredStateSubject,this.listingLocations
    ]).pipe(
      map(([states, listingLocations]) => {
        if (!listingLocations[states[0]]) return [];
        return Object.keys(listingLocations[states[0]])
      }),
      map(counties => this.formatCountyOptions(counties)),
      tap(() => {
        this.desiredCounties = [];
        this.desiredCountySubdivisions = [];
      })
    )

    this.filterCountySubdivisionOptions = combineLatest([
      this.desiredCountySubject, this.desiredStateSubject, this.listingLocations
    ]).pipe(
      filter(([counties, states]) => !!counties && !!counties.length && !!states && !!states.length),
      map(([counties, states, listingLocations]) => {
        return this.parseCityOrNeighborhoods(listingLocations, states, counties);
      }),
      map(this.stringsToMultiSelectOptions),
      tap(() => this.desiredCountySubdivisions = [])
    )

    fromEvent(this.applyButton.nativeElement, 'click')
      .subscribe(() => {

        let queryParams: any = {
          'beds[]': this.desiredBeds,
          'fullBaths[]': this.desiredFullBaths,
          'halfBaths[]': this.desiredHalfBaths,
          'sqft[]': this.desiredSqft,
          'statuses[]': this.desiredStatuses,
          'minPrice[]': this.desiredMinPrice,
          'maxPrice[]': this.desiredMaxPrice,
          'states[]': this.desiredStates,
          'counties[]': this.desiredCounties,
          'countySubdivisions[]': this.desiredCountySubdivisions
        }

        queryParams = omitBy(queryParams, (value: string[]) => !value.length);

        // https://github.com/angular/angular/issues/17609#issuecomment-441435372
        // Angular seems to not be recognizing all changes to queryParams as actual changes, and
        // hence does not reload the results. Not sure if this is due to us not subscribing to params
        // correctly or what, but this is a very easy solution somebody proposed for now.
        queryParams.v = Math.floor(Math.random() * 1000000000000)

        this.router.navigate(['/our-homes'], { queryParams });
      })
  }

  applyFilters() {

    this.loadingListings = true;

    // be aware this creates a new observable instance with every filter change.
    // TODO fix this by tying to a subject/ general refactor
    this.pins = this.listingsService
      .getPins(this.desiredListingOptions())
      .pipe(
        startWith([]),
        share(),
      )

    let listingsResponse = this.listingsService
      .getListings(this.desiredListingOptions())
      .pipe(
        share(),
        tap(() => {

          this.loadingListings = false;

          this.selectedBeds = clone(this.desiredBeds);
          this.selectedFullBaths = clone(this.desiredFullBaths);
          this.selectedHalfBaths = clone(this.desiredHalfBaths);
          this.selectedSqft = clone(this.desiredSqft);
          this.selectedStatuses = clone(this.desiredStatuses);
          this.selectedMinPrice = clone(this.desiredMinPrice);
          this.selectedMaxPrice = clone(this.desiredMaxPrice);
          this.selectedStates = clone(this.desiredStates);
          this.selectedCounties = clone(this.desiredCounties);
          this.selectedCountySubdivisions = clone(this.desiredCountySubdivisions);

          this.showingMobilePropertyFilters = false;
          this.showingMobileListingFilters = false;
          this.showingMobileLocationFilters = false;
        })
      )

    listingsResponse.pipe(
      tap((response: ListingsResponse) => {
        this.length = response.total_listings;
        this.showResults = !!this.length;
        this.showPaginator = this.length > this.pageSize
      }),
      map((response: ListingsResponse) => response.listings),
      map((listingTimelines: ListingTimelineable[]) => {
        return lodashMap(listingTimelines, listingTimeline => new ListingTimeline(listingTimeline))
      })
    )
    .subscribe((listingTimelines: ListingTimeline[]) => {

      // Note: We're subscribring to this Observable and setting it's result to a variable (instead of just leaving
      // it an Obserservable and using async pipe) to prevent async content from disappearing from the screen
      // when new Observable event starts. With this method, the existing content remains on the screen until the
      // Observable finishes and emits a new value...
      this.listingTimelines = listingTimelines;
    })
  }

  onStateChange(states: string[]) {
    this.desiredStateSubject.next(states);
  }

  onCountyChange(counties: string[]) {
    this.desiredCountySubject.next(counties);
  }

  unappliedFilterCount() {
    let unappliedFilterCount = 0;

    if (!isEqual(this.selectedBeds, this.desiredBeds)) unappliedFilterCount += 1;
    if (!isEqual(this.selectedFullBaths, this.desiredFullBaths)) unappliedFilterCount += 1;
    if (!isEqual(this.selectedHalfBaths, this.desiredHalfBaths)) unappliedFilterCount += 1;
    if (!isEqual(this.selectedSqft, this.desiredSqft)) unappliedFilterCount += 1;
    if (!isEqual(this.selectedStatuses, this.desiredStatuses)) unappliedFilterCount += 1;
    if (!isEqual(this.selectedMinPrice, this.desiredMinPrice)) unappliedFilterCount += 1;
    if (!isEqual(this.selectedMaxPrice, this.desiredMaxPrice)) unappliedFilterCount += 1;
    if (!isEqual(this.selectedStates, this.desiredStates)) unappliedFilterCount += 1;
    if (!isEqual(this.selectedCounties, this.desiredCounties)) unappliedFilterCount += 1;
    if (!isEqual(this.selectedCountySubdivisions, this.desiredCountySubdivisions)) unappliedFilterCount += 1;
    if (!isEqual(this.selectedSortFields, this.desiredSortFields)) unappliedFilterCount += 1;
    if (!isEqual(this.selectedSortDirections, this.desiredSortDirections)) unappliedFilterCount += 1;

    return unappliedFilterCount;
  }

  appliedPropertyFilterCount() {
    return this.selectedBeds.length + this.selectedFullBaths.length + this.selectedHalfBaths.length + this.selectedSqft.length;
  }

  appliedListingFilterCount() {
    return this.selectedStatuses.length + this.selectedMinPrice.length + this.selectedMaxPrice.length;
  }

  appliedLocationFilterCount() {
    return this.selectedStates.length + this.selectedCounties.length + this.selectedCountySubdivisions.length;
  }

  applyButtonText() {
    if (this.unappliedFilterCount()) {
      return `Apply (${this.unappliedFilterCount()}) Filters`;
    } else {
      return 'Apply';
    }
  }

  // https://github.com/angular/material2/issues/7203
  // Implementing multiple paginators for the same table takes a little bit of
  // work. This seems like a reasonable solution for now...sync the paginators
  // on pagination event and then just always use the top one for the data table
  // itself (see this.updateTimelines())
  handlePageChange(pageEvent: PageEvent) {

    this.pageSize = pageEvent.pageSize;
    this.pageIndex = pageEvent.pageIndex;
    this.length = pageEvent.length;

    this.topPaginator.pageIndex = this.pageIndex;
    this.topPaginator.pageSize = this.pageSize;
    this.topPaginator.length = this.length;

    this.bottomPaginator.pageIndex = this.pageIndex;
    this.bottomPaginator.pageSize = this.pageSize;
    this.bottomPaginator.length = this.length;

    this.applyFilters();
  }

  private stringsToMultiSelectOptions = (strings: string[]) => {
    return lodashMap(strings, string => ({ id: string, name: string }))
      .sort((a, b) => a.name.toLowerCase() > b.name.toLowerCase() ? 1 : -1) // alphabetical order
  }

  private isFilterByNeighborhoods(counties: string[]) {
    return counties[0] === 'Philadelphia' || counties[0] === 'District of Columbia' || counties[0] === 'Baltimore City';
  }

  // What gets populated in the Town/Neighborhood dropdown is dependent on the County and some
  // manual rules we specify.
  private parseCityOrNeighborhoods(listingLocations: ListingLocations, states: string[], counties: string[]) {

    // This filter in the rxjs chain should prevent getting here with unselected county and state, but just in case
    if (!listingLocations[states[0]][counties[0]]) return [];

    const cities = Object.keys(listingLocations[states[0]][counties[0]])

    // In a perfect world where we can fully trust our data, if we find a county that has a single city
    // we should go one more level down and return that single cities neighborhoods. Doing some extra checks
    if (this.isFilterByNeighborhoods(counties)) {

      this.filterCountySubdivisionTexts.next({
        defaultTitle: 'Any Neighborhood',
        searchEmptyResult: 'Please Select State and County',
        allSelected: 'All Neighborhoods',
        checkedPlural: 'neighborhoods'
      });

      // Note: It is assumed that if a county is being filtered by neighborhood, that county only has
      // a single town/city hence the cities[0] to access the neighborhoods
      return listingLocations[states[0]][counties[0]][cities[0]]

    } else {

      this.filterCountySubdivisionTexts.next({
        defaultTitle: 'Any Town',
        searchEmptyResult: 'Please Select State and County',
        allSelected: 'All Towns',
        checkedPlural: 'towns'
      });

      return cities;
    }
  }

  private formatStateOptions(states: string[]) {
    // Note: We need to consolodate our supported states and their mappings throughout
    // both the front and backends at some point.
    return SUPPORTED_STATES.filter(({ slug }) => states.includes(slug))
      .map(({ name, slug }) => ({ name, id: slug }))
      .sort((a, b) => a.name.toLowerCase() > b.name.toLowerCase() ? 1 : -1) // alphabetical order
  }

  private formatCountyOptions(counties: string[]): MultiSelectOption[] {
    return counties.map(county => ({
      id: county,
      name: county + ' County'
    })).sort((a, b) => a.name.toLowerCase() > b.name.toLowerCase() ? 1 : -1) // alphabetical order
  }

  private desiredListingOptions(): ListingOptions {
    return {
      min_bedrooms: this.desiredBeds[0],
      min_full_bathrooms: this.desiredFullBaths[0],
      min_half_bathrooms: this.desiredHalfBaths[0],
      min_sqft: this.desiredSqft[0],
      statuses: this.desiredStatuses,
      min_price: this.desiredMinPrice[0],
      max_price: this.desiredMaxPrice[0],
      states: this.desiredStates ? this.desiredStates : null,
      counties: this.desiredCounties ? this.desiredCounties : null,
      neighborhoods: (this.desiredCountySubdivisions && this.isFilterByNeighborhoods(this.desiredCounties)) ? this.desiredCountySubdivisions : null,
      towns: (this.desiredCountySubdivisions && !this.isFilterByNeighborhoods(this.desiredCounties)) ? this.desiredCountySubdivisions : null,
      page_index: this.pageIndex + 1, // Pagination being used server side (Kaminari ATM) uses 1 indexed pages, not zero
      page_size: this.pageSize,
      sort_field: this.desiredSortFields[0],
      sort_direction: this.desiredSortDirections[0],
    }
  }
}
